import { Card, CardContent, CardHeader } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import InfoTooltip from "@/components/ui/InfoTooltip"
import { WidgetTitle } from "@/components/widgets/WidgetTitle"
import { Skeleton } from "@/components/ui/skeleton"
import { useDashboard } from "@/contexts/DashboardContext"
import {
  fetchUserActivities,
  UserActivityRow,
} from "@/lib/axios/api/widgets.api"
import React from "react"
import ReactDOM from "react-dom"
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts"

const SERIES = [
  { key: "inbound", label: "Inbound", light: "hsl(var(--brand-navy))", dark: "hsl(var(--brand-navy))" },
  { key: "outbound", label: "Outbound", light: "#00FEC1", dark: "#00FEC1" },
  { key: "direct", label: "Inbound Direct", light: "hsl(var(--caution))", dark: "hsl(var(--caution))" },
  { key: "internal", label: "Internal", light: "#8E44AD", dark: "#8E44AD" },
]

const TOP_N_OPTIONS = [10, 15, 20, 30, 50]
const DEFAULT_TOP_N = 10
const SHOW_SELECTOR_THRESHOLD = 16
const BAR_SIZE = 13
const BAR_GAP = 20
const BAR_SLOT_WIDTH = BAR_SIZE + BAR_GAP
const OLD_BAR_SLOT_WIDTH = 72
const REDUCED_BAR_SLOT_WIDTH = 36
const OLD_BAR_SIZE = 30
const CHART_HEIGHT = 230
const X_AXIS_HEIGHT = 65
const MAX_LABEL_CHARS = 14
const COMPACT_LAYOUT_THRESHOLD = 30

function resolvePageSize(topN: number | "all", totalAgent: number): number {
  if (topN === "all") return totalAgent > 0 ? totalAgent : Math.max(...TOP_N_OPTIONS)
  return topN
}

function resolveIsCompactLayout(topN: number | "all", totalAgent: number): boolean {
  if (topN === "all") return totalAgent >= COMPACT_LAYOUT_THRESHOLD
  return topN >= COMPACT_LAYOUT_THRESHOLD
}

const REDUCED_BAR_WIDTH_THRESHOLD = 20

function resolveIsReducedBarWidth(topN: number | "all", totalAgent: number): boolean {
  if (topN === "all") return totalAgent >= REDUCED_BAR_WIDTH_THRESHOLD
  return topN >= REDUCED_BAR_WIDTH_THRESHOLD
}

function formatTopNLabel(topN: number | "all", totalAgent: number): string {
  if (topN === "all") return `All ${totalAgent}`
  return `Top ${Math.min(topN, totalAgent)} of ${totalAgent}`
}

function getChartThemeColors(theme: string) {
  const isDark = theme === "dark"
  return {
    axisColor: isDark ? "#94a3b8" : "#64748b",
    gridColor: isDark ? "#1e3a5f" : "#e2e8f0",
    tooltipBg: isDark ? "#0C3451" : "#fff",
    tooltipLabelColor: isDark ? "#fff" : "#0C3451",
    cursorFill: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
  }
}

function roundedTopRectPath(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): string {
  const r = Math.min(radius, width / 2)
  return `M${x},${y + r}
    Q${x},${y} ${x + r},${y}
    L${x + width - r},${y}
    Q${x + width},${y} ${x + width},${y + r}
    L${x + width},${y + height}
    L${x},${y + height}
    Z`
}

interface StackBarShapeProps {
  x?: number
  y?: number
  width?: number
  height?: number
  fill?: string
  seriesIndex?: number
  payload?: UserActivityRow
}

const StackBarShape: React.FC<StackBarShapeProps> = ({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  fill,
  seriesIndex = 0,
  payload,
}) => {
  if (height <= 0) return null
  const isTop = SERIES.slice(seriesIndex + 1).every(
    (other) => Number(payload?.[other.key as keyof UserActivityRow]) <= 0
  )
  if (!isTop) return <rect x={x} y={y} width={width} height={height} fill={fill} />
  const drawHeight = Math.max(height, 4)
  return <path d={roundedTopRectPath(x, y, width, drawHeight, 4)} fill={fill} />
}

function isAbortError(err: unknown): boolean {
  const name = (err as { name?: string })?.name
  return name === "CanceledError" || name === "AbortError"
}

/** Recharts custom tick with truncation + hover tooltip showing full name */
const CustomXTick = (props: {
  x?: number
  y?: number
  payload?: { value: string }
}) => {
  const { x = 0, y = 0, payload } = props
  const full = payload?.value ?? ""
  const gRef = React.useRef<SVGGElement>(null)
  const [tooltipPos, setTooltipPos] = React.useState<{ top: number; left: number } | null>(null)

  if (!full) return null
  const isTruncated = full.length > MAX_LABEL_CHARS
  const truncated = isTruncated ? full.slice(0, MAX_LABEL_CHARS) + "…" : full

  const handleMouseEnter = () => {
    if (!isTruncated || !gRef.current) return
    const rect = gRef.current.getBoundingClientRect()
    setTooltipPos({ top: rect.top, left: rect.left + rect.width / 2 })
  }

  return (
    <>
      <g
        ref={gRef}
        transform={`translate(${x},${y})`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setTooltipPos(null)}
      >
        <text
          x={0}
          y={0}
          dy={4}
          textAnchor="end"
          transform="rotate(-40)"
          fontSize={10}
          fontFamily="var(--font-family)"
          fill="currentColor"
        >
          {truncated}
        </text>
      </g>
      {tooltipPos &&
        ReactDOM.createPortal(
          <div
            className="fixed z-50 -translate-x-1/2 -translate-y-full rounded-md border bg-popover px-3 py-1.5 text-xs text-primary dark:text-white shadow-md pointer-events-none"
            style={{ top: tooltipPos.top - 6, left: tooltipPos.left }}
          >
            {full}
          </div>,
          document.body
        )}
    </>
  )
}

interface UserActivitiesChartWidgetProps {
  startDate?: string
  endDate?: string
  agentMicrosoftIds?: string[] | null
  queueMicrosoftIds?: string[] | null
}

function useUserActivitiesData({
  startDate,
  endDate,
  agentMicrosoftIds = null,
  queueMicrosoftIds = null,
}: UserActivitiesChartWidgetProps) {
  const [topN, setTopN] = React.useState<number | "all">(DEFAULT_TOP_N)
  const [totalAgent, setTotalAgent] = React.useState(0)
  const [chartData, setChartData] = React.useState<UserActivityRow[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [isError, setIsError] = React.useState(false)

  const pageSize = React.useMemo(
    () => resolvePageSize(topN, totalAgent),
    [topN, totalAgent]
  )

  React.useEffect(() => {
    setTopN(DEFAULT_TOP_N)
    setTotalAgent(0)
  }, [startDate, endDate, agentMicrosoftIds, queueMicrosoftIds])

  React.useEffect(() => {
    if (!startDate || !endDate) return

    const controller = new AbortController()
    setIsLoading(true)
    setIsError(false)

    fetchUserActivities(
      { startDate, endDate, agentMicrosoftIds, queueMicrosoftIds, pageSize },
      { signal: controller.signal }
    )
      .then((result) => {
        setTotalAgent(result.totalAgent)
        setChartData(result.agents)
      })
      .catch((err) => {
        if (isAbortError(err)) return
        setIsError(true)
        setChartData([])
        setTotalAgent(0)
      })
      .finally(() => setIsLoading(false))

    return () => controller.abort()
  }, [startDate, endDate, agentMicrosoftIds, queueMicrosoftIds, pageSize])

  React.useEffect(() => {
    if (totalAgent > 0 && totalAgent < SHOW_SELECTOR_THRESHOLD && topN !== "all") {
      setTopN("all")
    }
  }, [totalAgent, topN])

  const isCompactLayout = React.useMemo(
    () => resolveIsCompactLayout(topN, totalAgent),
    [topN, totalAgent]
  )
  const isReducedBarWidth = React.useMemo(
    () => resolveIsReducedBarWidth(topN, totalAgent),
    [topN, totalAgent]
  )

  const barSlotWidth = isReducedBarWidth ? REDUCED_BAR_SLOT_WIDTH : OLD_BAR_SLOT_WIDTH
  const fitToContainer = totalAgent > 0 && totalAgent < SHOW_SELECTOR_THRESHOLD
  const chartWidth = isCompactLayout
    ? chartData.length * BAR_SLOT_WIDTH + 60
    : Math.max(400, chartData.length * barSlotWidth + 60)

  return {
    topN,
    setTopN,
    totalAgent,
    chartData,
    isLoading,
    isError,
    showTopSelector: totalAgent >= SHOW_SELECTOR_THRESHOLD,
    fitToContainer,
    isCompactLayout,
    isReducedBarWidth,
    chartWidth,
  }
}

interface ActivitiesTopSelectorProps {
  topN: number | "all"
  totalAgent: number
  onTopNChange: (value: number | "all") => void
}

const ActivitiesTopSelector: React.FC<ActivitiesTopSelectorProps> = ({
  topN,
  totalAgent,
  onTopNChange,
}) => (
  <div className="flex items-center gap-1.5 text-xs text-muted-foreground dark:text-white/60 shrink-0">
    <span>{formatTopNLabel(topN, totalAgent)}</span>
    <Select
      value={String(topN)}
      onValueChange={(v) => onTopNChange(v === "all" ? "all" : Number(v))}
    >
      <SelectTrigger className="h-7 w-[88px] text-xs px-2 py-0 dark:bg-[#3C5C73] dark:border-white/30 dark:text-white [&_svg]:size-3">
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end" className="min-w-[5rem]">
        <SelectItem value="all" className="text-xs py-1">All</SelectItem>
        {TOP_N_OPTIONS.map((n, i) => {
          const smallerOptionAlreadyCovers = TOP_N_OPTIONS.slice(0, i).some((m) => m >= totalAgent)
          return (
            <SelectItem
              key={n}
              value={String(n)}
              disabled={smallerOptionAlreadyCovers}
              className="text-xs py-1 data-[disabled]:opacity-100 data-[disabled]:text-muted-foreground/60"
            >
              Top {n}
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  </div>
)

const ActivitiesLegend: React.FC<{ theme: string; isLoading: boolean }> = ({
  theme,
  isLoading,
}) => {
  const isDark = theme === "dark"

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-4 px-4 pt-3 pb-1 flex-wrap flex-shrink-0">
        {SERIES.map((s) => (
          <div key={s.key} className="flex items-center gap-1.5">
            <Skeleton className="w-3 h-3 shrink-0" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center gap-4 px-4 pt-3 pb-1 flex-wrap flex-shrink-0">
      {SERIES.map((s) => {
        const color = isDark ? s.dark : s.light
        return (
          <div key={s.key} className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 shrink-0" style={{ background: color }} />
            <span className="text-sm" style={{ color }}>{s.label}</span>
          </div>
        )
      })}
    </div>
  )
}

interface ActivitiesTooltipContentProps {
  active?: boolean
  payload?: Array<{ dataKey?: string | number; name?: string; value?: number | string; color?: string }>
  label?: string
  theme?: string
}

const ActivitiesTooltipContent: React.FC<ActivitiesTooltipContentProps> = ({
  active,
  payload,
  label,
  theme = "light",
}) => {
  const chartTheme = getChartThemeColors(theme)
  if (!active || !payload?.length || !label) return null
  return (
    <div
      style={{
        backgroundColor: chartTheme.tooltipBg,
        border: "1px solid #e2e8f0",
        borderRadius: 6,
        fontSize: 14,
        padding: 12,
      }}
    >
      <p style={{ color: chartTheme.tooltipLabelColor, fontWeight: 600, margin: 0 }}>
        {label}
      </p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color, margin: 0 }}>
          {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  )
}

interface ActivitiesBarChartProps {
  chartData: UserActivityRow[]
  chartWidth: number
  theme: string
  colors: Record<string, string>
  isCompactLayout: boolean
  isReducedBarWidth: boolean
  fitToContainer: boolean
}

function getBarCategoryGap(isCompactLayout: boolean, isReducedBarWidth: boolean): number | string {
  if (isCompactLayout) return 0
  return isReducedBarWidth ? "6.25%" : "12.5%"
}

const ActivitiesBarChart: React.FC<ActivitiesBarChartProps> = ({
  chartData,
  chartWidth,
  theme,
  colors,
  isCompactLayout,
  isReducedBarWidth,
  fitToContainer,
}) => {
  const chartTheme = getChartThemeColors(theme)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const [availableWidth, setAvailableWidth] = React.useState<number | null>(null)

  React.useEffect(() => {
    if (!isCompactLayout) return
    const el = wrapperRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width
      if (width) setAvailableWidth(width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [isCompactLayout])

  const needsScroll = availableWidth !== null && chartWidth > availableWidth
  const barCategoryGap = getBarCategoryGap(isCompactLayout, isReducedBarWidth)
  const slotCount = (() => {
    if (isCompactLayout && availableWidth !== null && !needsScroll) {
      return Math.max(chartData.length, Math.floor((availableWidth - 60) / BAR_SLOT_WIDTH))
    }
    if (fitToContainer) {
      return Math.max(chartData.length, DEFAULT_TOP_N)
    }
    return chartData.length
  })()
  const displayData =
    (isCompactLayout || fitToContainer) && slotCount > chartData.length
      ? [
          ...chartData,
          ...Array.from({ length: slotCount - chartData.length }, () => ({
            agent: "",
            inbound: 0,
            outbound: 0,
            direct: 0,
            internal: 0,
          })),
        ]
      : chartData

  const chart = (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={displayData}
        margin={
          isCompactLayout
            ? { top: 4, right: 8, left: 8, bottom: 8 }
            : { top: 4, right: 8, left: -16, bottom: 8 }
        }
        barCategoryGap={barCategoryGap}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={chartTheme.gridColor}
          vertical={false}
        />
        <XAxis
          dataKey="agent"
          tick={<CustomXTick />}
          interval={0}
          height={X_AXIS_HEIGHT}
          axisLine={{ stroke: chartTheme.gridColor }}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 10, fontFamily: "var(--font-family)", fill: "currentColor" }}
          allowDecimals={false}
          axisLine={false}
          tickLine={false}
        />
        <RechartsTooltip
          contentStyle={{
            backgroundColor: chartTheme.tooltipBg,
            border: "1px solid #e2e8f0",
            borderRadius: 6,
          }}
          labelStyle={{
            color: chartTheme.tooltipLabelColor,
            fontWeight: 600,
          }}
          cursor={{ fill: chartTheme.cursorFill }}
          content={<ActivitiesTooltipContent theme={theme} />}
        />
        {SERIES.map((s, idx) =>
          isCompactLayout ? (
            <Bar
              key={s.key}
              dataKey={s.key}
              stackId="a"
              fill={colors[s.key]}
              name={s.label}
              barSize={BAR_SIZE}
              shape={<StackBarShape seriesIndex={idx} />}
            />
          ) : (
            <Bar
              key={s.key}
              dataKey={s.key}
              stackId="a"
              fill={colors[s.key]}
              name={s.label}
              barSize={isReducedBarWidth ? OLD_BAR_SIZE : undefined}
              shape={<StackBarShape seriesIndex={idx} />}
            />
          )
        )}
      </BarChart>
    </ResponsiveContainer>
  )

  let chartContent: React.ReactNode
  if (fitToContainer) {
    chartContent = chart
  } else if (isCompactLayout && needsScroll) {
    chartContent = <div style={{ width: chartWidth, height: "100%" }}>{chart}</div>
  } else if (isCompactLayout) {
    chartContent = chart
  } else if (!isReducedBarWidth) {
    // Bar width unset (auto-fit) at this topN — Recharts already scales bars
    // to the container, so force full width instead of a scroll wrapper.
    chartContent = chart
  } else {
    chartContent = (
      <div style={{ width: chartWidth, minWidth: "100%", height: "100%" }}>
        {chart}
      </div>
    )
  }

  return (
    <div
      ref={wrapperRef}
      className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden px-2"
      style={{ minHeight: CHART_HEIGHT + X_AXIS_HEIGHT }}
    >
      {chartContent}
    </div>
  )
}

interface ActivitiesChartBodyProps {
  isLoading: boolean
  isError: boolean
  chartData: UserActivityRow[]
  totalAgent: number
  chartWidth: number
  theme: string
  colors: Record<string, string>
  isCompactLayout: boolean
  isReducedBarWidth: boolean
  fitToContainer: boolean
}

const ActivitiesChartBody: React.FC<ActivitiesChartBodyProps> = ({
  isLoading,
  isError,
  chartData,
  totalAgent,
  chartWidth,
  theme,
  colors,
  isCompactLayout,
  isReducedBarWidth,
  fitToContainer,
}) => {
  if (isLoading) {
    return (
      <div className="flex-1 flex flex-col gap-2 px-4 py-6">
        <Skeleton className="h-full min-h-[200px] w-full" />
      </div>
    )
  }

  if (isError || totalAgent === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground py-6">
        No call records were found
      </div>
    )
  }

  return (
    <ActivitiesBarChart
      chartData={chartData}
      chartWidth={chartWidth}
      theme={theme}
      colors={colors}
      isCompactLayout={isCompactLayout}
      isReducedBarWidth={isReducedBarWidth}
      fitToContainer={fitToContainer}
    />
  )
}

const UserActivitiesChartWidget: React.FC<UserActivitiesChartWidgetProps> = ({
  startDate,
  endDate,
  agentMicrosoftIds,
  queueMicrosoftIds,
}) => {
  const { theme } = useDashboard()
  const {
    topN,
    setTopN,
    totalAgent,
    chartData,
    isLoading,
    isError,
    showTopSelector,
    fitToContainer,
    isCompactLayout,
    isReducedBarWidth,
    chartWidth,
  } = useUserActivitiesData({ startDate, endDate, agentMicrosoftIds, queueMicrosoftIds })

  const colors = React.useMemo(
    () =>
      Object.fromEntries(
        SERIES.map((s) => [s.key, theme === "dark" ? s.dark : s.light])
      ),
    [theme]
  )

  return (
    <Card className="w-full h-full overflow-clip bg-white dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] flex flex-col">
      <CardHeader className="dark:border-b flex-shrink-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 shrink-0">
            <WidgetTitle>Activities</WidgetTitle>
            <InfoTooltip content="Call volume per user, split by direction within given time. Hover a bar for the exact breakdown" />
          </div>
          {showTopSelector && (
            <ActivitiesTopSelector
              topN={topN}
              totalAgent={totalAgent}
              onTopNChange={setTopN}
            />
          )}
        </div>
      </CardHeader>

      <CardContent className="p-0 flex flex-col flex-1 min-h-0">
        <ActivitiesLegend theme={theme} isLoading={isLoading} />
        <ActivitiesChartBody
          isLoading={isLoading}
          isError={isError}
          chartData={chartData}
          totalAgent={totalAgent}
          chartWidth={chartWidth}
          theme={theme}
          colors={colors}
          isCompactLayout={isCompactLayout}
          isReducedBarWidth={isReducedBarWidth}
          fitToContainer={fitToContainer}
        />
      </CardContent>
    </Card>
  )
}

export default UserActivitiesChartWidget
